/** Helpers for instructor auto-assign availability (poligon vs sector). */

import {
   DEFAULT_DISTRICTS,
   isPolygonDistrict,
   sectorRequiresPolygon,
} from "./districts";

// Implicit; numărul real vine de la server (GET /settings/booking →
// myPolygonLessons): adminul îl poate schimba pentru elevii noi.
export const MANDATORY_POLIGON_LESSONS = 7;

/** Sectorul ales cere lec\u021bii pe poligon (sectoarele vin de la GET /districts). */
export function sectorRequiresPoligon(sector, districts = DEFAULT_DISTRICTS) {
   return sectorRequiresPolygon(districts, sector);
}

export function normalizeGearboxLabel(raw) {
   const g = String(raw ?? "")
      .trim()
      .toLowerCase();
   if (g.startsWith("auto")) return "Automat";
   return "Manual";
}

/** Normalize "7:00" / "07:00:00" → "07:00" for map keys. */
export function normalizeSlotTime(raw) {
   const text = String(raw ?? "").trim();
   if (!text) return "";
   const match = text.match(/^(\d{1,2}):(\d{2})/);
   if (!match) return text;
   const hh = String(Math.max(0, Math.min(23, Number(match[1]) || 0))).padStart(
      2,
      "0",
   );
   const mm = String(Math.max(0, Math.min(59, Number(match[2]) || 0))).padStart(
      2,
      "0",
   );
   return `${hh}:${mm}`;
}

export function normalizeAvailabilityDate(raw) {
   const text = String(raw ?? "").trim();
   if (!text) return "";
   const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
   return match ? `${match[1]}-${match[2]}-${match[3]}` : text.slice(0, 10);
}

export function availabilitySlotKey(date, time) {
   const d = normalizeAvailabilityDate(date);
   const t = normalizeSlotTime(time);
   if (!d || !t) return "";
   return `${d}|${t}`;
}

function reservationStartMs(r) {
   const raw =
      r?.startTime ??
      r?.startDate ??
      r?.start ??
      r?.reservation?.startTime ??
      r?.reservation?.startDate ??
      null;
   if (!raw) return 0;
   const ms = new Date(raw).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function reservationInstructorId(r) {
   const raw =
      r?.instructorId ??
      r?.instructor_id ??
      r?.instructor?.id ??
      r?.reservation?.instructorId ??
      r?.reservation?.instructor_id ??
      r?.reservation?.instructor?.id ??
      null;
   const n = Number(raw);
   return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Instructor with the most lessons in the list (ties go to the most recent
 * lesson) — the student's de-facto "preferred" instructor. Null when no
 * reservation carries an instructor id.
 */
export function dominantInstructorIdFromReservations(reservations = []) {
   const stats = new Map(); // id -> { count, lastMs }
   for (const r of reservations || []) {
      const id = reservationInstructorId(r);
      if (!id) continue;
      const cur = stats.get(id) || { count: 0, lastMs: 0 };
      cur.count += 1;
      cur.lastMs = Math.max(cur.lastMs, reservationStartMs(r));
      stats.set(id, cur);
   }
   let best = null;
   for (const [id, s] of stats) {
      if (
         !best ||
         s.count > best.count ||
         (s.count === best.count && s.lastMs > best.lastMs)
      ) {
         best = { id, ...s };
      }
   }
   return best ? best.id : null;
}

/** Lecțiile deja făcute pe orice poligon (anulatele se numără, ca pe server). */
export function countPolygonReservations(
   reservations = [],
   districts = DEFAULT_DISTRICTS,
) {
   let count = 0;
   for (const r of reservations || []) {
      const sector =
         r?.sector ??
         r?.reservation?.sector ??
         r?.instructor?.sector ??
         r?.instructorSector ??
         "";
      if (isPolygonDistrict(districts, sector)) count += 1;
   }
   return count;
}

export function remainingPoligonLessonsNeeded(
   sector,
   existingReservations = [],
   requiredCount = MANDATORY_POLIGON_LESSONS,
   districts = DEFAULT_DISTRICTS,
) {
   if (!sectorRequiresPoligon(sector, districts)) return 0;
   const required = Number.isInteger(Number(requiredCount))
      ? Math.max(0, Number(requiredCount))
      : MANDATORY_POLIGON_LESSONS;
   const done = countPolygonReservations(existingReservations, districts);
   return Math.max(0, required - done);
}

/**
 * Build Map<"YYYY-MM-DD|HH:mm", { poligonFreeInstructors, sectorFreeInstructors }>
 * from GET /reservations/availability response.
 * Hours/days not present in the response are intentionally absent (treated as blocked).
 */
export function buildAvailabilitySlotMap(availabilityResponse) {
   const map = new Map();
   const days = Array.isArray(availabilityResponse?.days)
      ? availabilityResponse.days
      : [];

   for (const day of days) {
      const date = normalizeAvailabilityDate(day?.date);
      if (!date) continue;
      const slots = Array.isArray(day?.slots) ? day.slots : [];
      for (const slot of slots) {
         const time = normalizeSlotTime(slot?.time);
         if (!time) continue;
         const key = availabilitySlotKey(date, time);
         if (!key) continue;
         map.set(key, {
            poligonFreeInstructors: Math.max(
               0,
               Number(slot?.poligonFreeInstructors) || 0,
            ),
            sectorFreeInstructors: Math.max(
               0,
               Number(slot?.sectorFreeInstructors) || 0,
            ),
         });
      }
   }
   return map;
}

/**
 * When startTimes are sorted chronologically, the first `remainingPoligon`
 * entries are poligon lessons. Returns true if `candidateIso` would fall
 * into that poligon prefix among selected + candidate.
 */
export function candidateNeedsPoligon({
   candidateIso,
   selectedIsos = [],
   remainingPoligon = 0,
}) {
   const need = Math.max(0, Number(remainingPoligon) || 0);
   if (need <= 0) return false;

   const candidateTs = new Date(candidateIso).getTime();
   if (!Number.isFinite(candidateTs)) return false;

   const allTs = [];
   for (const iso of selectedIsos || []) {
      const ts = new Date(iso).getTime();
      if (Number.isFinite(ts)) allTs.push(ts);
   }
   allTs.push(candidateTs);
   allTs.sort((a, b) => a - b);

   const idx = allTs.indexOf(candidateTs);
   return idx >= 0 && idx < need;
}

/**
 * Relevant free-instructor count for the student's current lesson stage.
 * Missing meta → -1 (slot absent from API = blocked).
 */
export function getRelevantFreeCount(
   slotMeta,
   {
      requiresPoligon = false,
      remainingPoligon = 0,
      candidateIso = null,
      selectedIsos = [],
      /** Force pool: 'poligon' | 'sector' | null (auto from chronology). */
      forcePool = null,
   } = {},
) {
   if (!slotMeta || typeof slotMeta !== "object") return -1;

   if (forcePool === "poligon") {
      return Math.max(0, Number(slotMeta.poligonFreeInstructors) || 0);
   }
   if (forcePool === "sector") {
      return Math.max(0, Number(slotMeta.sectorFreeInstructors) || 0);
   }

   const usePoligon =
      Boolean(requiresPoligon) &&
      Number(remainingPoligon) > 0 &&
      candidateNeedsPoligon({
         candidateIso,
         selectedIsos,
         remainingPoligon,
      });

   if (usePoligon) {
      return Math.max(0, Number(slotMeta.poligonFreeInstructors) || 0);
   }
   return Math.max(0, Number(slotMeta.sectorFreeInstructors) || 0);
}

/**
 * true only when relevant free count > 0.
 * Missing slotMeta (not in API list) → false (blocked).
 */
export function isAvailabilitySlotFree({
   slotMeta,
   candidateIso,
   selectedIsos = [],
   remainingPoligon = 0,
   requiresPoligon = false,
   forcePool = null,
} = {}) {
   const count = getRelevantFreeCount(slotMeta, {
      requiresPoligon,
      remainingPoligon,
      candidateIso,
      selectedIsos,
      forcePool,
   });
   return count > 0;
}

export function dayHasAnyFreeSlot({
   dayLocal,
   slotLabels = [],
   availabilityMap,
   selectedIsos = [],
   remainingPoligon = 0,
   requiresPoligon = false,
   forcePool = null,
   buildCandidateIso,
} = {}) {
   const day = normalizeAvailabilityDate(dayLocal);
   if (!day || typeof buildCandidateIso !== "function") return false;
   if (!(availabilityMap instanceof Map) || availabilityMap.size === 0) {
      return false;
   }

   for (const time of slotLabels) {
      const normTime = normalizeSlotTime(time);
      const key = availabilitySlotKey(day, normTime);
      if (!key) continue;
      const meta = availabilityMap.get(key);
      const iso = buildCandidateIso(day, normTime);
      if (!iso) continue;
      if (
         isAvailabilitySlotFree({
            slotMeta: meta,
            candidateIso: iso,
            selectedIsos,
            remainingPoligon,
            requiresPoligon,
            forcePool,
         })
      ) {
         return true;
      }
   }

   // Day present-but-full or missing entirely → not selectable.
   return false;
}

export function hasEligibleInstructors(eligiblePayload) {
   const sectorCount = Array.isArray(eligiblePayload?.sectorInstructors)
      ? eligiblePayload.sectorInstructors.length
      : 0;
   const poligonCount = Array.isArray(eligiblePayload?.poligonInstructors)
      ? eligiblePayload.poligonInstructors.length
      : 0;
   // poligonSector null: elevul și-a făcut deja lecțiile pe poligon (sau
   // trebuie întâi să aleagă poligonul) — nu-i trebuie instructori de poligon
   const requiresPoligon =
      Boolean(eligiblePayload?.requiresPoligon) &&
      eligiblePayload?.poligonSector !== null;

   if (requiresPoligon) {
      return sectorCount > 0 && poligonCount > 0;
   }
   return sectorCount > 0;
}

function sortIsosAsc(isos = []) {
   return [...(isos || [])].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
   );
}

export function availabilityConflictReason({
   slotMeta,
   candidateIso,
   selectedIsos = [],
   remainingPoligon = 0,
   requiresPoligon = false,
   forcePool = null,
} = {}) {
   const usePoligon =
      forcePool === "poligon" ||
      (forcePool !== "sector" &&
         Boolean(requiresPoligon) &&
         Number(remainingPoligon) > 0 &&
         candidateNeedsPoligon({
            candidateIso,
            selectedIsos,
            remainingPoligon,
         }));
   const count = getRelevantFreeCount(slotMeta, {
      requiresPoligon,
      remainingPoligon,
      candidateIso,
      selectedIsos,
      forcePool,
   });
   if (count < 0) return "Ora nu apare în disponibilitatea curentă.";
   if (count === 0) {
      return usePoligon
         ? "Indisponibil pe poligon (0 instructori liberi)."
         : "Indisponibil pe sector (0 instructori liberi).";
   }
   return "Intervalul nu mai este liber.";
}

/**
 * Split selection into free vs blocked against the current availability map.
 *
 * Each slot is judged in the context of the FULL current set (not a growing
 * subset), so poligon vs sector assignment stays consistent.
 *
 * When `stabilize` is true, drop blocked slots and re-check the remainder
 * until the kept set is internally consistent — avoids the drip-feed where
 * fixing 1–2 conflicts then reveals 1–2 new ones on the next submit.
 */
export function partitionSlotsByAvailability({
   selectedIsos = [],
   availabilityMap,
   remainingPoligon = 0,
   requiresPoligon = false,
   slotKeyForIso,
   stabilize = true,
} = {}) {
   if (typeof slotKeyForIso !== "function") {
      return { ok: [], conflicts: [] };
   }

   let remaining = sortIsosAsc(selectedIsos);
   const allConflicts = [];
   const seenConflict = new Set();

   const runPass = (isos) => {
      const ok = [];
      const conflicts = [];
      for (const iso of isos) {
         const key = slotKeyForIso(iso);
         const meta =
            key && availabilityMap instanceof Map
               ? availabilityMap.get(key)
               : undefined;
         const peers = isos.filter((x) => x !== iso);
         const free = isAvailabilitySlotFree({
            slotMeta: meta,
            candidateIso: iso,
            selectedIsos: peers,
            remainingPoligon,
            requiresPoligon,
         });
         if (free) ok.push(iso);
         else {
            conflicts.push({
               iso,
               reason: availabilityConflictReason({
                  slotMeta: meta,
                  candidateIso: iso,
                  selectedIsos: peers,
                  remainingPoligon,
                  requiresPoligon,
               }),
            });
         }
      }
      return { ok, conflicts };
   };

   // Hard cap: selection size is small (≤30); stabilize needs at most N passes.
   for (let pass = 0; pass < 64; pass += 1) {
      const { ok, conflicts } = runPass(remaining);
      for (const c of conflicts) {
         if (seenConflict.has(c.iso)) continue;
         seenConflict.add(c.iso);
         allConflicts.push(c);
      }
      if (!stabilize || conflicts.length === 0) {
         return { ok, conflicts: allConflicts };
      }
      remaining = ok;
      if (remaining.length === 0) {
         return { ok: [], conflicts: allConflicts };
      }
   }

   return { ok: remaining, conflicts: allConflicts };
}
